<template>
  <div class="movie-style-container">
    <!-- 顶部工具栏 -->
    <div class="toolbar">
      <el-input 
        v-model="searchKeyword" 
        placeholder="搜索作品..." 
        class="search-input"
        @keyup.enter.native="handleSearch">
        <el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
      </el-input>
      <el-button type="primary" @click="addWork">
        <i class="el-icon-plus"></i> 添加作品
      </el-button>
    </div>
    
    <!-- 作品网格 -->
    <div class="works-grid">
      <div class="work-card" v-for="work in workList" :key="work.id">
        <div class="card-inner">
          <!-- 作品封面 -->
          <div class="poster-container">
            <img 
              :src="work.imageUrl || defaultWorkImage" 
              class="poster" 
              alt="作品封面">
            <div class="overlay">
              <div class="actions">
                <el-button type="text" icon="el-icon-edit" @click="editWork(work)">编辑</el-button>
                <el-button type="text" icon="el-icon-upload" @click="uploadImage(work)">更换封面</el-button>
                <el-button type="text" icon="el-icon-delete" @click="deleteWork(work)">删除</el-button>
              </div>
            </div>
          </div>
          
          <!-- 作品信息 -->
          <div class="work-info">
            <h3 class="title">{{ work.name }}</h3>
            <div class="meta">
              <span class="role-count"><i class="el-icon-user"></i> {{ work.roleCount || 0 }} 角色</span>
              <span class="tag-count"><i class="el-icon-tags"></i> {{ work.tagCount || 0 }} 标签</span>
            </div>
            <el-button 
              type="text" 
              class="view-details" 
              @click="viewRoles(work)">
              查看角色 <i class="el-icon-arrow-right"></i>
            </el-button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 空状态 -->
    <div v-if="workList.length === 0 && !loading" class="empty-state">
      <i class="el-icon-film"></i>
      <p>暂无作品数据</p>
      <el-button type="primary" @click="addWork">创建作品</el-button>
    </div>
    
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-state">
      <el-loading-spinner size="large"></el-loading-spinner>
      <p>加载中...</p>
    </div>
    
    <!-- 分页 -->
    <div v-if="total > 0 && !loading" class="pagination">
      <el-pagination
        :current-page="page"
        :page-size="pageSize"
        :total="total"
        layout="total, prev, pager, next"
        @current-change="handlePageChange">
      </el-pagination>
    </div>
  </div>
</template>

<script>
import defaultWorkImage from '@/assets/default-work.png';
import { getWorks, searchWorks, deleteWork } from '@/api/work';

export default {
  name: 'WorkListView',
  data() {
    return {
      workList: [],
      total: 0,
      page: 1,
      pageSize: 12,
      searchKeyword: '',
      loading: false,
      defaultWorkImage
    };
  },
  created() {
    this.loadWorkList();
  },
  methods: {
    async loadWorkList() {
      this.loading = true;
      try {
        let response;
        const params = { page: this.page, size: this.pageSize };
        
        if (this.searchKeyword) {
          response = await searchWorks({ keyword: this.searchKeyword, ...params });
        } else {
          response = await getWorks(params);
        }
        
        this.workList = response.data.data.list;
        this.total = response.data.data.total;
      } catch (error) {
        console.error('加载作品失败:', error);
        this.$message.error('加载作品失败，请重试');
      } finally {
        this.loading = false;
      }
    },

    handleSearch() {
      this.page = 1;
      this.loadWorkList();
    },

    handlePageChange(page) {
      this.page = page;
      this.loadWorkList();
    },

    addWork() {
      this.$router.push('/work-form');
    },

    editWork(work) {
      this.$router.push({ path: '/work-form', query: { id: work.id } });
    },

    viewRoles(work) {
      this.$router.push({ path: '/roles', query: { workId: work.id } });
    },

    uploadImage(work) {
      this.$router.push({ path: '/work-cover', query: { workId: work.id, currentCover: work.imageUrl } });
    },

    async deleteWork(work) {
      try {
        await this.$confirm(`确定删除作品 "${work.name}" 吗?`, '确认删除', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });
        
        await deleteWork(work.id);
        this.$message.success('作品删除成功');
        this.loadWorkList();
      } catch (error) {
        // 取消删除或删除失败
      }
    }
  }
};
</script>

<style scoped>
.movie-style-container {
  padding: 20px;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.search-input {
  width: 300px;
}

.works-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 25px;
}

.work-card {
  transition: transform 0.3s, box-shadow 0.3s;
  height: 100%;
}

.work-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 20px rgba(0, 0, 0, 0.1);
}

.card-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
}

.poster-container {
  position: relative;
  padding-top: 140%; /* 1.4:1的宽高比，电影海报常见比例 */
  overflow: hidden;
}

.poster {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s;
}

.work-card:hover .poster {
  transform: scale(1.05);
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 50%);
  opacity: 0;
  transition: opacity 0.3s;
  display: flex;
  align-items: flex-end;
}

.work-card:hover .overlay {
  opacity: 1;
}

.actions {
  width: 100%;
  padding: 15px;
  display: flex;
  justify-content: space-around;
}

.actions .el-button {
  color: white;
}

.work-info {
  padding: 15px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.title {
  font-size: 16px;
  margin: 0 0 10px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.meta {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #889099;
  margin-bottom: 15px;
}

.view-details {
  margin-top: auto;
  align-self: flex-start;
  color: #409eff;
  padding: 0;
}

.empty-state {
  text-align: center;
  padding: 80px 0;
  color: #889099;
}

.empty-state i {
  font-size: 60px;
  margin-bottom: 20px;
}

.loading-state {
  text-align: center;
  padding: 80px 0;
}

.pagination {
  margin-top: 30px;
  text-align: center;
}

@media (max-width: 768px) {
  .works-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  }
  
  .toolbar {
    flex-direction: column;
    gap: 10px;
    align-items: stretch;
  }
  
  .search-input {
    width: 100%;
  }
}
</style>